import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { invoicingService } from '../services/invoicingService';

const InvoiceEditModal = ({ open, onClose, invoice, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Invoice header fields
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState([]);

  // Calculate totals
  const [subtotal, setSubtotal] = useState(0);
  const [totalVat, setTotalVat] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (invoice && open) {
      // Populate form with invoice data
      setInvoiceDate(invoice.invoice_date || '');
      setDueDate(invoice.due_date || '');
      setPaidDate(invoice.paid_date || '');
      setStatus(invoice.status || 'draft');
      setNotes(invoice.notes || '');
      setTermsAndConditions(invoice.terms_and_conditions || '');
      setCustomerName(invoice.customer_name || '');
      setCustomerEmail(invoice.customer_email || '');
      setCustomerPhone(invoice.customer_phone || '');

      // Populate line items
      if (invoice.line_items && invoice.line_items.length > 0) {
        setLineItems(invoice.line_items.map((item, index) => {
          // Auto-detect MOT from description
          const description = item.description || '';
          const isMot = item.is_mot_service || description.toLowerCase().includes('mot');

          return {
            id: item.id,
            description: description,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            vat_rate: isMot ? 0 : (parseFloat(item.vat_rate) || 20),
            is_mot_service: isMot,
            service_category: item.service_category || '',
            sort_order: item.sort_order || index,
          };
        }));
      } else {
        setLineItems([createEmptyLineItem()]);
      }
    }
  }, [invoice, open]);

  useEffect(() => {
    // Calculate totals when line items change
    let subTotal = 0;
    let vatTotal = 0;

    lineItems.forEach(item => {
      const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
      const vatAmount = (lineTotal * (item.vat_rate || 0)) / 100;
      subTotal += lineTotal;
      vatTotal += vatAmount;
    });

    setSubtotal(subTotal);
    setTotalVat(vatTotal);
    setTotalAmount(subTotal + vatTotal);
  }, [lineItems]);

  const createEmptyLineItem = () => ({
    id: null,
    description: '',
    quantity: 1,
    unit_price: 0,
    vat_rate: 20,
    is_mot_service: false,
    service_category: '',
    sort_order: lineItems.length,
  });

  const handleAddLineItem = () => {
    setLineItems([...lineItems, createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      const item = lineItems[index];
      const itemDesc = item.description || `Item ${index + 1}`;
      if (window.confirm(`Remove "${itemDesc}" from the invoice?`)) {
        setLineItems(lineItems.filter((_, i) => i !== index));
      }
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    // Auto-set VAT rate for MOT services
    if (field === 'is_mot_service' && value === true) {
      updatedItems[index].vat_rate = 0;
    } else if (field === 'is_mot_service' && value === false) {
      updatedItems[index].vat_rate = 20;
    }

    // Auto-detect MOT from description
    if (field === 'description' && value.toLowerCase().includes('mot')) {
      updatedItems[index].is_mot_service = true;
      updatedItems[index].vat_rate = 0;
    }

    setLineItems(updatedItems);
  };

  const calculateLineTotal = (item) => {
    return (item.quantity || 0) * (item.unit_price || 0);
  };

  const calculateLineVat = (item) => {
    const lineTotal = calculateLineTotal(item);
    return (lineTotal * (item.vat_rate || 0)) / 100;
  };

  const calculateLineTotalWithVat = (item) => {
    return calculateLineTotal(item) + calculateLineVat(item);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const buildInvoiceData = () => {
    return {
      invoice_date: invoiceDate,
      due_date: dueDate,
      paid_date: paidDate || null,
      status: status,
      notes: notes,
      terms_and_conditions: termsAndConditions,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      line_items: lineItems.map((item, index) => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        vat_rate: parseFloat(item.vat_rate) || 0,
        is_mot_service: item.is_mot_service,
        service_category: item.service_category,
        sort_order: index,
      })),
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = buildInvoiceData();
      await invoicingService.updateInvoiceFull(invoice.id, data);
      onSave && onSave();
      onClose();
    } catch (err) {
      setError('Failed to save invoice: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndResend = async () => {
    if (!window.confirm(`Save changes and resend invoice to ${customerEmail}?`)) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const data = buildInvoiceData();
      await invoicingService.editAndResend(invoice.id, data);
      onSave && onSave();
      onClose();
    } catch (err) {
      setError('Failed to save and resend invoice: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Edit Invoice: {invoice.invoice_number}
          </Typography>
          <Box display="flex" gap={1}>
            {invoice.edit_count > 0 && (
              <Chip
                label={`Edited ${invoice.edit_count}x`}
                size="small"
                color="warning"
              />
            )}
            <Chip
              label={invoice.status}
              size="small"
              color={invoice.status === 'paid' ? 'success' : 'default'}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Customer Info Section */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Customer Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Phone"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Invoice Details Section */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Invoice Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Invoice Date"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Paid Date"
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Line Items Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Line Items
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddLineItem}
          >
            Add Item
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ width: '30%' }}>Description</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>Qty</TableCell>
                <TableCell align="right" sx={{ width: '12%' }}>Unit Price</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>VAT %</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>MOT</TableCell>
                <TableCell align="right" sx={{ width: '12%' }}>VAT</TableCell>
                <TableCell align="right" sx={{ width: '12%' }}>Total</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="Enter description"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      size="small"
                      variant="standard"
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                      sx={{ width: 60 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      size="small"
                      variant="standard"
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={item.vat_rate}
                      onChange={(e) => handleLineItemChange(index, 'vat_rate', parseFloat(e.target.value) || 0)}
                      size="small"
                      variant="standard"
                      inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                      sx={{ width: 50 }}
                      disabled={item.is_mot_service}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <input
                      type="checkbox"
                      checked={item.is_mot_service}
                      onChange={(e) => handleLineItemChange(index, 'is_mot_service', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(calculateLineVat(item))}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {formatCurrency(calculateLineTotalWithVat(item))}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLineItem(index)}
                      disabled={lineItems.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 250 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Subtotal:</Typography>
              <Typography>{formatCurrency(subtotal)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>VAT:</Typography>
              <Typography>{formatCurrency(totalVat)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight="bold">Total:</Typography>
              <Typography fontWeight="bold" color="primary">
                {formatCurrency(totalAmount)}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notes Section */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Notes & Terms
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Terms and Conditions"
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
              multiline
              rows={3}
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save Only
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSaveAndResend}
          disabled={saving}
        >
          Save & Resend
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceEditModal;
